import DateInput from '@/common/components/base/input/dateInput/DateInput';
import TitleDescription from '@/common/components/base/typography/TitleDescription';
import TimeInput from '@/common/components/base/input/timeInput/TimeInput';
import MainLayout from '@/common/components/layouts/MainLayout';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, Flex } from 'styled-system/jsx';
import PercentageInput from '@/common/components/base/percentageInput/PercentageInput';
import { requiredText } from '@/common/utils/rules/publicRules';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import LoadingButton from '@/common/components/base/button/loadingButton';
import { VisitInformationForm } from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVisitInformation/getVisitInformationQuer.type';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useVisitList } from '@/common/hooks/useVisitList';
import { usePatchVisitInformationMutation } from '@/modules/adjusterReportModules/IncidentParticipants/queries/patchVisitInformation/patchVisitInformationQueryOption';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import getOtherVisitInformation from '@/modules/adjusterReportModules/IncidentParticipants/queries/getVisitOtherDamagedInformation/getVisitOtherDamagedInformationQueryOption';
import { validateNotPastDate } from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import TextInputWithSelectOnMap from '@/common/components/base/input/mapInput/TextInputWithSelectOnMap';
import NumberInputWithWords from '@/common/components/base/numberInputWithWords/NumberInputWithWords';
import convertTimestampToShamsi from '@/common/utils/convertTimestampToShamsi';

export default () => {
  return (
    <MainLayout title="زیان‌دیده / کارشناسی" header="titleHeader">
      <OtherDamagedVisitInformation />
    </MainLayout>
  );
};
const OtherDamagedVisitInformation = () => {
  const { id, damagedId } = useParams();
  const navigate = useNavigate();
  const { mutate: patchVisitformation, isPending: loading } =
    usePatchVisitInformationMutation();
  useQuery({
    ...getOtherVisitInformation({
      damaged_id: damagedId!,
      id: id!,
      source: 'vehicle',
    }),
    meta: {
      onSuccess: (data) => {
        if (data) {
          methods.reset({
            visit_site: {
              value: data.visit_site?.const,
              label: data.visit_site?.fa_const,
            },
            visit_location: data?.visit_location ?? undefined,
            visit_location_phi: data?.visit_location_phi ?? null,
            visit_location_lambda: data?.visit_location_lambda ?? null,
            visit_date: !!data?.visit_date
              ? convertTimestampToShamsi(data?.visit_date)
              : null,
            visit_time: data?.visit_time,
            distance: !!data?.distance ? data?.distance : 0,
            visit_number: data?.visit_number,
          });
        }
        if (data?.visit_date) {
          setTimeout(() => {
            methods.trigger('visit_date');
          });
        }
      },
    },
  });
  const { visitListQuery } = useVisitList();

  const methods = useForm<VisitInformationForm>({
    mode: 'onChange',
  });
  const onSubmit = (
    formData: Omit<VisitInformationForm, 'parts_repair_length'>,
  ) => {
    const formattedData = {
      ...formData,
      visit_location: formData?.visit_location,
      visit_date: convertPersianToGregorian(
        formData?.visit_date?.toString() ?? '',
      ),
      visit_site: formData.visit_site?.value,
      location_phi: formData?.visit_location_phi ?? null,
      location_lambda: formData?.visit_location_lambda ?? null,
    };
    patchVisitformation(
      {
        damaged_id: damagedId!,
        id: id!,
        data: formattedData,
      },
      {
        onSuccess: () => {
          navigate(-1);
        },
      },
    );
  };

  return (
    <Box width="100%" pt={'24px'} position="relative">
      <TitleDescription>اطلاعات بازدید</TitleDescription>
      <FormProvider {...methods}>
        <Flex direction="column" gap={8} mt={24}>
          <Box>
            <DateInput
              label="تاریخ بازدید"
              name="visit_date"
              buttonText="انتخاب تاریخ"
              modalLabel="انتخاب تاریخ تولد"
              rules={{ required: requiredText, validate: validateNotPastDate }}
              minDate={new Date(Date.now() - 24 * 60 * 60 * 1000)}
            />
          </Box>
          <Box>
            <TimeInput
              buttonText="ثبت ساعت بازدید"
              modalLabel="انتخاب زمان بازدید"
              name="visit_time"
              label="ساعت بازدید"
              rules={{ required: requiredText }}
            />
          </Box>
          <Box>
            <PercentageInput
              name="visit_number"
              label="تعداد بازدید"
              percentage={1}
              maxPercentage={1000}
              placeHolder="تعداد بازدید را وارد کنید"
            />
          </Box>
          <Box>
            <ModalInput
              name="visit_site"
              label="محل بازدید"
              placeholder="انتخاب کنید"
              options={visitListQuery.data || []}
              defaultValue=""
            />
          </Box>
          <Box>
            <NumberInputWithWords
              label="مسافت طی شده (کیلومتر )"
              name="distance"
              placeholder="مسافت طی شده را وارد کنید"
              showPriceToWords={false}
              hasSeparator={false}
            />
          </Box>
          <Box>
            <TextInputWithSelectOnMap
              rules={{ required: requiredText }}
              label="آدرس محل بازدید"
              entranceModalText={
                'در ادامه آدرس محل وقوع حادثه را بر روی نقشه مشخص کنید .'
              }
              mapModalTitle="محل بازدید"
              name="visit_location"
              phiName="visit_location_phi"
              lambdaName="visit_location_lambda"
            />
          </Box>
          <Box pb={24}>
            <LoadingButton
              onClick={methods.handleSubmit(onSubmit)}
              loading={loading}
              fullWidth
              disabled={!methods?.formState?.isValid}
            >
              ثبت
            </LoadingButton>
          </Box>
        </Flex>
      </FormProvider>
    </Box>
  );
};
